import { nativeSessionMark } from '#features/api/utils/nativeAuth';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { removePushToken } from '../model/notifications';
import { beginNativePushSignOut, settleNativePushRegistrations } from './nativePushRegistration';

/**
 * Releases this device's push token on sign-out.
 *
 * An FCM token belongs to the browser (or the app installation), not to the session: it survives
 * a logout untouched and comes back identical under the next account. Without this call the row
 * stays attached to whoever signed in first, so the device keeps rendering that account's
 * notifications while somebody else is using it.
 *
 * Must run while the session cookie is still alive — call it before the logout request.
 * Never throws: failing to detach must not block signing out.
 */
export const detachPushToken = async () => {
  beginNativePushSignOut();
  await settleNativePushRegistrations();
  const credential = envConfig.IS_NATIVE ? await nativeSessionMark() : null;
  const token = localStorage.getItem('fcm_token');

  if (!token) {
    // Nothing to release, but the signature must still go: left behind, it would tell the sync
    // hook this device is already registered and the next account would never claim it.
    localStorage.deleteItem('fcm_token_signature');
    return;
  }

  try {
    if (credential) await removePushToken({ token }, credential);
    else await removePushToken({ token });
  } catch (error) {
    console.warn('[push] failed to detach push token on logout', error);
  } finally {
    localStorage.deleteItem('fcm_token');
    // The sync hook skips re-registering when its signature still matches. Leaving it behind
    // would mean the row this call just deleted is never re-created on the next sign-in: no
    // pushes at all, and every incoming call going straight to missed because the server sees
    // no device to ring.
    localStorage.deleteItem('fcm_token_signature');
  }
};
